import React, { ChangeEvent, useEffect, useState } from 'react';
import styled from 'styled-components';
import { regexRegistrationPlate } from '~/helpers/regex';
import {
  mq,
  TextField,
  Button,
  Stack,
  Caption,
  Link
} from '@kvdbil/components';
import { Color } from '@kvdbil/components/types/Types';
import { useTranslation } from '~/Locale';
import { translateRoute } from '../shared/localization';
import { TextFieldProps } from '@kvdbil/components/types/components/Textfield';

const Container = styled(Stack)`
  width: 100%;
  color: ${({ theme }) => theme.colors.text.dark};
`;

const Heading = styled.p`
  color: ${({ theme }) => theme.colors.text.dark};
  text-align: center;
`;

const ResponsiveStack = styled(Stack)<{ $inline?: boolean }>`
  width: 100%;

  & button {
    width: ${({ $inline }) => ($inline ? 'auto' : '100%')};
    ${({ $inline }) => $inline && 'flex-shrink: 0;'}
  }

  ${({ $inline }) =>
    $inline &&
    `
    & > *:first-child {
      flex: 1 1 auto;
      min-width: 0;
    }
  `}

  margin: auto;

  ${mq('tablet')} {
    margin: auto;
  }
`;

const HelperText = styled(Caption)`
  text-align: center;
`;

// Prevent clicking the link when the button is loading by setting pointer-events to none
const LinkWrapper = styled.span<{ $isLoading?: boolean }>`
  pointer-events: ${({ $isLoading }) => ($isLoading ? 'none' : 'auto')};
  opacity: ${({ $isLoading }) => ($isLoading ? 0.5 : 1)};
  cursor: ${({ $isLoading }) => ($isLoading ? 'not-allowed' : 'pointer')};
`;

const DarkBtnText = styled.span`
  color: ${({ theme }) => theme.colors.text.dark};
`;

interface Props {
  sellingText?: string;
  title: string;
  label?: string;
  css?: string;
  value?: string;
  linkToHeavyEquipment?: boolean;
  onSubmit(registrationPlate: string): void;
  inputProps?: TextFieldProps;
  isLoading?: boolean;
  inline?: boolean;
  submitColor?: Color;
}

const RegistrationPlateInput = ({
  title,
  sellingText,
  label,
  onSubmit,
  inputProps,
  value = '',
  linkToHeavyEquipment,
  isLoading,
  inline,
  submitColor = 'secondary',
  ...props
}: Props) => {
  const [registrationPlate, setRegistrationPlate] = useState(value);
  const [localError, setLocalError] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    setRegistrationPlate(value.toUpperCase());
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.toUpperCase();
    setRegistrationPlate(newValue);
    if (localError) {
      setLocalError(null);
    }
    if (inputProps?.onChange) {
      inputProps.onChange?.(e);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedPlate = registrationPlate.replace(/\s+/g, '');
    const isRegistrationPlateValid = regexRegistrationPlate.test(trimmedPlate);

    if (!isRegistrationPlateValid) {
      setLocalError(t('Invalid registration number'));
      return;
    }

    setLocalError(null);
    onSubmit(trimmedPlate);
  };

  const hasError = Boolean(localError || inputProps?.hasError);
  const errorMessage =
    localError ||
    (inputProps?.errors?.[0] ? String(inputProps?.errors?.[0]) : undefined);

  const textFieldProps = {
    ...inputProps,
    hasError,
    errors: errorMessage ? [errorMessage] : undefined
  };

  return (
    <Container {...props} spacing={1}>
      {sellingText && <Heading>{sellingText}</Heading>}
      <form noValidate onSubmit={handleSubmit}>
        <ResponsiveStack
          spacing={inline ? 1 : 0.5}
          direction={inline ? 'row' : 'column'}
          align={inline ? 'flex-start' : undefined}
          $inline={inline}
        >
          <TextField
            onChange={handleChange}
            value={registrationPlate}
            placeholder={t('Enter license number')}
            label={label}
            required
            {...textFieldProps}
          />

          <Button
            color={submitColor}
            colorShade={submitColor === 'primary' ? 'main' : 'mainAlt'}
            size="regular"
            isDisabled={isLoading}
            isLoading={isLoading}
            type="submit"
          >
            {submitColor === 'primary' ? (
              <DarkBtnText>{title}</DarkBtnText>
            ) : (
              title
            )}
          </Button>
        </ResponsiveStack>
      </form>

      {linkToHeavyEquipment && (
        <HelperText>
          {t('Heavy vehicle or machine with missing registration number?')}{' '}
          <LinkWrapper $isLoading={isLoading}>
            <Link
              path={translateRoute(t, '/ROUTES.SELL_HEAVY_EQUIPMENT')}
              text={t('Go here.')}
              typographyType="Caption"
              withUnderline
            />
          </LinkWrapper>
        </HelperText>
      )}
    </Container>
  );
};

export default RegistrationPlateInput;
